import React, { useState, useEffect, useRef } from 'react';
import { X, Maximize2 } from 'lucide-react';

/**
 * HowToInfographic — renders one of the 4 illustrated "How to use this
 * tool" guides that live in /public/infographics/:
 *
 *   /public/infographics/zakat.png        →  Personal Zakat calculator
 *   /public/infographics/inheritance.png  →  Inheritance (Faraid) calculator
 *   /public/infographics/will.png         →  Islamic Will (Wasiyyah) writer
 *   /public/infographics/kit.png          →  Estate Document Kit
 *
 * Placed just above the compact "learn" strips on each calculator page and
 * next to the KitDownloadButton on the homepage. Users can tap to open a
 * larger lightbox view (helpful on mobile). Escape or click-outside closes
 * the lightbox.
 *
 * Props:
 *   src       — public path to the PNG
 *   alt       — accessible alt-text
 *   caption?  — optional caption below the image
 *   testid?   — data-testid prefix (default 'how-to-infographic')
 *   variant?  — 'boxed' (default, card) | 'plain' (no card chrome)
 */
export const HowToInfographic = ({
  src,
  alt,
  caption,
  testid = 'how-to-infographic',
  variant = 'boxed',
}) => {
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog so keyboard users can Escape immediately
    // and screen readers announce the modal.
    setTimeout(() => closeBtnRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shell = variant === 'boxed'
    ? 'rounded-2xl border border-stone-200 bg-white p-2 sm:p-3 shadow-[0_4px_24px_-16px_rgba(6,78,59,0.15)]'
    : '';

  return (
    <>
      <figure data-testid={testid} className={shell}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testid}-thumb`}
          className="group relative block w-full rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
          aria-label={`Open larger view: ${alt}`}
        >
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="w-full h-auto max-h-[420px] object-contain bg-white"
          />
          <span className="pointer-events-none absolute top-2 end-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#EDF5F1] text-[#0D1520] text-[10px] font-mono uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="w-3 h-3" /> Expand
          </span>
        </button>
        {caption && (
          <figcaption className="mt-2 text-[11.5px] text-stone-500 text-center leading-snug px-2">
            {caption}
          </figcaption>
        )}
      </figure>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          data-testid={`${testid}-lightbox`}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(43,36,25,0.55)] backdrop-blur-sm p-3 sm:p-6"
        >
          <button
            type="button"
            ref={closeBtnRef}
            onClick={() => setOpen(false)}
            data-testid={`${testid}-close`}
            className="absolute top-3 end-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[rgba(43,36,25,0.35)] text-[#0D1520] hover:bg-black focus-visible:ring-2 focus-visible:ring-gold-400 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain shadow-2xl rounded-lg bg-white"
          />
        </div>
      )}
    </>
  );
};

export default HowToInfographic;
